import React from "react";
import PropTypes from "prop-types";
// Material imports
// import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { useLocation } from "react-router-dom";

// Limitless imports
import { LoadingSpinner, PortalTextInput, PortalButton, AutoComplete, createSuggestions, findSuggestionFromList } from "../..";
import { validateIdVerification } from "../../../expert";
import UserIdVerificationResult from "./UserIdVerificationResult";
const useStyles = makeStyles(theme => {
  return {
    dobContainer: {
      rowGap: 3,
      marginBottom: 8,
      backgroundColor: theme.palette.background.elevation1
    },
    locationContainer: {
      rowGap: 3,
      marginBottom: 16,
      marginLeft: 4
    },
    textColor: {
      color: theme.palette.text.secondary
    },
    container: {
      backgroundColor: theme.palette.background.elevation1
    },
    disabled: {
      color: theme.palette.action.disabled
    },
    link: {
      cursor: "pointer",
      fontWeight: 600
    },
    resultText: {
      marginTop: 8
    },
    fieldContainer: {
      paddingRight: 24,
      paddingLeft: 8,
      rowGap: 10,
      marginBottom: 24
    }
  };
});
const UserIdVerificationOverview = ({
  t,
  readOnly,
  profileFields,
  processedFields,
  verificationData,
  updateVerificationField,
  getBirthDays,
  getBirthMonths,
  getBirthYears,
  verifyIdentity
}) => {
  var _profileFields$countr;
  const classes = useStyles();
  const {
    displayGroups
  } = processedFields;
  const location = useLocation();
  const isOnboardingDetail = location.pathname.includes("/onboarding/detail");
  const DROPDOWN_VALUES = {
    dayOfBirth: createSuggestions(getBirthDays(), {
      labelDataKey: "label",
      valueDataKey: "value"
    }),
    monthOfBirth: createSuggestions(getBirthMonths(), {
      labelDataKey: "label",
      valueDataKey: "value"
    }),
    yearOfBirth: createSuggestions(getBirthYears(), {
      labelDataKey: "label",
      valueDataKey: "value"
    })
  };
  const LOCATION_VALUES = ((_profileFields$countr = profileFields.countrySubDivisions) === null || _profileFields$countr === void 0 ? void 0 : _profileFields$countr.length) > 0 ? createSuggestions(profileFields.countrySubDivisions, {
    labelDataKey: "Name",
    valueDataKey: "Code"
  }) : null;
  const {
    fieldErrors,
    isFormInvalid
  } = validateIdVerification(verificationData);
  const renderTextField = field => {
    const hasError = field.match === false;
    return /*#__PURE__*/React.createElement(Grid, {
      item: true,
      key: field.name,
      md: 6
    }, /*#__PURE__*/React.createElement(PortalTextInput, {
      name: field.name,
      label: t(field.label),
      variant: "caption",
      id: field.name,
      value: verificationData[field.name].value || "",
      helperText: t(field.description),
      isMandatory: field.required,
      autoComplete: "off",
      onChange: event => updateVerificationField(field.name, event.target.value),
      error: hasError,
      errorLabel: hasError,
      disabled: readOnly,
      inputProps: isOnboardingDetail ? {
        style: {
          padding: "0px"
        }
      } : ""
    }));
  };
  const renderFieldTitle = React.useCallback((title, isMandatory) => {
    return /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      style: {
        paddingBottom: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      component: "div",
      color: "textPrimary",
      className: readOnly ? classes.disabled : ""
    }, t(title), isMandatory && /*#__PURE__*/React.createElement("span", {
      style: {
        color: theme => theme.palette.error.main,
        display: "inline-flex",
        position: "relative",
        bottom: 4,
        marginLeft: 3
      }
    }, "*")));
  }, [readOnly]);
  const renderDateOfBirth = () => {
    const dayField = processedFields.displayGroups.dob.find(f => f.name === "dayOfBirth");
    const monthField = processedFields.displayGroups.dob.find(f => f.name === "monthOfBirth");
    const yearField = processedFields.displayGroups.dob.find(f => f.name === "yearOfBirth");
    return /*#__PURE__*/React.createElement(Grid, {
      container: true,
      spacing: 2,
      className: classes.dobContainer,
      key: "dob"
    }, renderFieldTitle("Date of Birth", true), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sm: 3,
      md: 2,
      style: {
        paddingTop: 0
      }
    }, /*#__PURE__*/React.createElement(AutoComplete, {
      value: verificationData.dayOfBirth.value || null,
      placeholder: t("day"),
      id: "dayOfBirth",
      suggestions: DROPDOWN_VALUES["dayOfBirth"],
      onChange: selected => updateVerificationField("dayOfBirth", selected),
      name: "dayOfBirth",
      disabled: readOnly,
      error: (dayField === null || dayField === void 0 ? void 0 : dayField.match) === false
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sm: 4,
      md: 3,
      style: {
        paddingTop: 0
      }
    }, /*#__PURE__*/React.createElement(AutoComplete, {
      value: verificationData.monthOfBirth.value || null,
      placeholder: t("month"),
      id: "monthOfBirth",
      suggestions: DROPDOWN_VALUES["monthOfBirth"],
      onChange: selected => updateVerificationField("monthOfBirth", selected),
      name: "monthOfBirth",
      disabled: readOnly,
      error: (monthField === null || monthField === void 0 ? void 0 : monthField.match) === false
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sm: 3,
      lg: 2,
      style: {
        paddingTop: 0
      }
    }, /*#__PURE__*/React.createElement(AutoComplete, {
      value: verificationData.yearOfBirth.value || null,
      placeholder: t("year"),
      id: "yearOfBirth",
      suggestions: DROPDOWN_VALUES["yearOfBirth"],
      onChange: selected => updateVerificationField("yearOfBirth", selected),
      name: "yearOfBirth",
      disabled: readOnly,
      error: (yearField === null || yearField === void 0 ? void 0 : yearField.match) === false
    })), (fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors.dob) && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      style: {
        paddingTop: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, null, t(fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors.dob))));
  };
  const renderLocation = field => {
    const value = verificationData[field.name] ? findSuggestionFromList(LOCATION_VALUES, verificationData[field.name]) : null;
    return /*#__PURE__*/React.createElement(Grid, {
      container: true,
      spacing: 1,
      className: classes.locationContainer,
      key: "location"
    }, renderFieldTitle(field.label, field.required), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sm: 6,
      md: 6,
      style: {
        paddingTop: 0
      }
    }, /*#__PURE__*/React.createElement(AutoComplete, {
      value: value,
      placeholder: t(field.description),
      id: field.name,
      suggestions: LOCATION_VALUES,
      onChange: selected => updateVerificationField("stateProvinceCode", selected.value),
      name: t(field.name),
      disabled: readOnly
    })), (fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors.location) && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      style: {
        paddingTop: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "error"
    }, fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors.location)));
  };
  return profileFields.loading ? /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "center",
    alignItems: "center",
    style: {
      height: "80%"
    }
  }, /*#__PURE__*/React.createElement(LoadingSpinner, {
    size: 30,
    color: "primary"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    className: classes.fieldContainer,
    width: isOnboardingDetail ? "100%" : "75%"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.textColor
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      marginBottom: 16
    }
  }, t("As part of the onboarding process, Limitless verifies the identity of each user on our platform to ensure we comply with local regulation and minimise the risk of fraudulent activity on our platform. Please provide your personal details truthfully and as per official documentation, otherwise we may be unable to verify your identity. Your name, date of birth and address details will be stored on your user profile and can be updated in future."))), processedFields.processed && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    style: {
      rowGap: 10
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    className: classes.container
  }, displayGroups.name.length > 0 && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    style: {
      rowGap: 10
    },
    key: "name",
    className: classes.container
  }, displayGroups.name.map(field => {
    return renderTextField(field);
  })), displayGroups.dob.length > 0 && renderDateOfBirth(), displayGroups.otherPersonal.length > 0 && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    style: {
      rowGap: 10,
      marginBottom: 8
    },
    key: "otherPersonal"
  }, displayGroups.otherPersonal.map(field => {
    return renderTextField(field);
  })), displayGroups.location.length > 0 && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    style: {
      rowGap: 10
    },
    key: "location"
  }, displayGroups.location.map(field => {
    if (LOCATION_VALUES.length > 0) {
      return renderLocation(field);
    }
    return renderTextField(field);
  })), displayGroups.address.length > 0 && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    style: {
      rowGap: 10,
      paddingBottom: 8
    },
    key: "address",
    className: classes.container
  }, displayGroups.address.map(field => {
    return renderTextField(field);
  }))), displayGroups.other.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.textColor
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      marginBottom: 16
    }
  }, t("The personal information captured below is used by a 3rd party vendor for identity verification purposes only. Neither Limitless nor this 3rd party store the personal information provided below once identity verification is complete. The identity verification process may leave a soft footprint on your credit file, which will not impact your credit score"), t(" - for more information, click here "), " ", /*#__PURE__*/React.createElement("span", {
    onClick: () => window.open(t("https://credit-hyperlink"), "_new"),
    className: classes.link
  }, t("[credit-hyperlink-text]")), t(". Certain fields may be required, whereas other fields may be optional - please note: providing more information improves the likelihood that your identity can be verified."))), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    style: {
      rowGap: 10
    },
    key: "other",
    className: classes.container
  }, displayGroups.other.map(field => {
    return renderTextField(field);
  })))), !readOnly && !profileFields.verified && profileFields.verificationConfigured && profileFields.verificationAttemptRemaining > 0 && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    item: true,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    name: "terms",
    id: "termsCheckbox",
    checked: verificationData["terms"].value || false,
    onClick: () => {
      updateVerificationField("terms", !verificationData["terms"].value);
    },
    value: verificationData["terms"].value
  }), /*#__PURE__*/React.createElement(Typography, {
    onClick: () => updateVerificationField("terms", !verificationData["terms"].value),
    id: "termsButton",
    variant: "body2",
    color: theme => theme.palette.text.primary
  }, t("I consent to a 3rd party vendor processing my personal data for Identity Verification purposes only."))), !readOnly && processedFields.processed && !readOnly && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center"
  }, !profileFields.verificationConfigured ? /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React.createElement(PortalButton, {
    onClick: verifyIdentity,
    disabled: isFormInvalid,
    loading: !processedFields.processed,
    id: "saveProfile"
  }, t("Save my Profile"))) : /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React.createElement(PortalButton, {
    onClick: verifyIdentity,
    disabled: isFormInvalid,
    loading: !processedFields.processed,
    id: "verifyMyId"
  }, t("VERIFY MY ID")))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.resultText
  }, /*#__PURE__*/React.createElement(UserIdVerificationResult, {
    t: t,
    profileFields: profileFields
  }))));
};
export default UserIdVerificationOverview;
UserIdVerificationOverview.propTypes = {
  t: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  profileFields: PropTypes.object.isRequired,
  processedFields: PropTypes.object.isRequired,
  verificationData: PropTypes.object.isRequired,
  updateVerificationField: PropTypes.func.isRequired,
  getBirthDays: PropTypes.func.isRequired,
  getBirthMonths: PropTypes.func.isRequired,
  getBirthYears: PropTypes.func.isRequired,
  verifyIdentity: PropTypes.func.isRequired
};